/**
 * @file SessionSummaryPanel.tsx
 * @brief The consultation summary — beside the paper, never on it.
 *
 * @details
 * Phase E of `docs/product/medical-session-summary-plan.md`.
 *
 * ## It is outside the sheet, and that is the whole design
 *
 * ⚠️ Everything inside `MlrSheet` looks like the report because it **is** the
 * report — Times, justified, page-numbered, drawn to be recognised as a court
 * document. Putting a record of what was said on that paper would make it look
 * like expert evidence at a glance, which is the exact confusion this feature
 * was built to avoid.
 *
 * So it uses Meeting Intelligence's chrome: `GridBand` headings and full-width
 * `GridCell` rows (Overview, Executive summary, then the grounded sections),
 * not a nested card column sitting on the paper. The visual difference from
 * Times-on-A4 is carrying an argument.
 *
 * ## The label is served
 *
 * The title and disclaimer come from the API with the payload. They are part
 * of the deliverable, not UI strings — and a heading typed here could drift
 * from the one the phone shows and the one the partner displays.
 *
 * @references
 * - BACKEND/src/features/medical-assessment/sessionSummary.schema.mjs
 * - FRONTEND/.../SessionSummaryCard.tsx — the same thing on the phone
 * - WEBPANEL/.../MeetingDetailView.tsx SummaryTab — the layout this copies
 * @author DES
 * @date 18-09-2026
 * @updated 19-09-2026
 */

import { useState, type ComponentType } from "react";
import {
  ChevronDown,
  CircleCheck,
  FileText,
  Lightbulb,
  ListTodo,
  Sparkles,
  TriangleAlert,
} from "lucide-react";

import {
  GridBand,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { cn } from "@/shared/theme/cn";
import { useSessionSummary, type MlrSummaryItem } from "../api";

type BandIcon = ComponentType<{ className?: string }>;

const SECTION_ICONS: Record<string, BandIcon> = {
  "Key points": Lightbulb,
  "Agreed in the consultation": CircleCheck,
  "What happens next": ListTodo,
  "Flagged as unresolved": TriangleAlert,
};

export function SessionSummaryPanel({ assessmentId }: { assessmentId: string }) {
  const { data } = useSessionSummary(assessmentId);
  const [open, setOpen] = useState(true);

  const summary = data?.summary ?? null;

  /*
   * ⚠️ Nothing renders when there is no summary — no empty card, no "not
   * available". Most consultations will not have one, and a permanent
   * placeholder would make the normal case look unfinished on every report
   * anyone opens.
   */
  const sections: Array<{ title: string; items: MlrSummaryItem[] }> = summary
    ? [
        { title: "Key points", items: summary.keyPoints ?? [] },
        { title: "Agreed in the consultation", items: summary.agreed ?? [] },
        { title: "What happens next", items: summary.followUps ?? [] },
        { title: "Flagged as unresolved", items: summary.attention ?? [] },
      ].filter((section) => section.items.length > 0)
    : [];

  const narrative = (summary?.narrative ?? []).filter((paragraph) =>
    paragraph.trim(),
  );
  const hasProse = Boolean(summary?.overview?.trim()) || narrative.length > 0;

  if (!summary || (sections.length === 0 && !hasProse)) return null;

  const label = data?.label ?? { title: "", disclaimer: "" };
  const languages = (summary.languages ?? []).map((l) => l.name).join(" · ");
  /*
   * Always four cells, including zeros — Meeting Intelligence's At a glance
   * is `grid-cols-3` whether a count is 0 or not. Filtering empties left two
   * stretched cells and a hollow middle on a wide rail. (19-09-2026)
   */
  const glance = [
    { value: summary.keyPoints?.length ?? 0, label: "Key points" },
    { value: summary.agreed?.length ?? 0, label: "Agreed" },
    { value: summary.followUps?.length ?? 0, label: "Next" },
    { value: summary.attention?.length ?? 0, label: "Unresolved" },
  ];

  return (
    <>
      {/*
          `p-0 md:px-0` is load-bearing. GridBand ships `px-4 md:px-5`;
          `p-0` alone does not beat the `md:` padding, so the button sat
          inset and hover painted the middle while the rail sides stayed
          empty. (19-09-2026)
       */}
      <GridBand className="p-0 md:px-0">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex w-full items-start justify-between gap-3 px-4 py-4 text-left transition-colors hover:bg-muted/40 md:px-5"
        >
          <span className="min-w-0">
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Sparkles className="h-4 w-4 text-brand" aria-hidden />
              {label.title}
            </span>
            <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
              {label.disclaimer}
            </span>
          </span>
          <ChevronDown
            className={cn(
              "mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none",
              open && "rotate-180",
            )}
            aria-hidden
          />
        </button>
      </GridBand>

      {open ? (
        <>
          {summary.overview ? (
            <GridBand className="bg-muted/25">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Overview
              </p>
              <p className="text-[17px] font-medium leading-8 tracking-tight text-foreground">
                {summary.overview}
              </p>
            </GridBand>
          ) : null}

          {/*
              Meeting Intelligence's shape: context, then the narrative, then
              the sections. ⚠️ The prose is written from the quoted items below
              and from nothing else, so it cannot contain a fact they do not.
           */}
          {narrative.length > 0 ? (
            <GridBand>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-2.5">
                <h4 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-foreground">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-soft text-brand">
                    <FileText className="h-3.5 w-3.5" aria-hidden />
                  </span>
                  Executive summary
                </h4>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
                  {narrative.length}{" "}
                  {narrative.length === 1 ? "passage" : "passages"}
                </span>
              </div>
              <div className="space-y-4">
                {narrative.map((paragraph, index) => (
                  <p
                    key={`para-${index}`}
                    className="text-[15px] leading-7 text-foreground/90"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </GridBand>
          ) : null}

          <div className="border-b border-r border-border/60 bg-muted/25 px-4 py-2.5 md:px-5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              At a glance
            </p>
          </div>
          <GridCells className="grid-cols-2 sm:grid-cols-4">
            {glance.map((cell) => (
              <GridCell
                key={cell.label}
                className="transition-colors hover:bg-muted/40"
              >
                <p className="font-mono text-2xl font-semibold tabular-nums tracking-tight text-foreground">
                  {cell.value}
                </p>
                <p className="mt-0.5 text-[11px] font-medium text-muted-foreground">
                  {cell.label}
                </p>
              </GridCell>
            ))}
          </GridCells>

          {sections.map((section) => {
            const Icon = SECTION_ICONS[section.title] ?? Lightbulb;
            return (
              <SummarySection
                key={section.title}
                title={section.title}
                icon={Icon}
                items={section.items}
              />
            );
          })}

          {languages || summary.clarity != null ? (
            <GridBand className="bg-muted/25 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {languages ? <>Spoken in {languages}</> : null}
                {languages && summary.clarity != null ? " · " : null}
                {summary.clarity != null ? (
                  <>Clarity {summary.clarity}/100</>
                ) : null}
              </p>
            </GridBand>
          ) : null}
        </>
      ) : null}
    </>
  );
}

function SummarySection({
  title,
  icon: Icon,
  items,
}: {
  title: string;
  icon: BandIcon;
  items: MlrSummaryItem[];
}) {
  return (
    <>
      <GridBand className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-foreground">
          <Icon className="h-4 w-4 text-brand" aria-hidden />
          {title}
        </h3>
        <span className="rounded-full bg-brand-soft px-2 py-0.5 text-[10px] font-semibold tabular-nums text-brand">
          {items.length} {items.length === 1 ? "item" : "items"}
        </span>
      </GridBand>
      <GridCells className="grid-cols-1">
        {items.map((item, index) => (
          <GridCell
            key={`${title}-${index}-${item.quote.slice(0, 16)}`}
            padded={false}
          >
            <QuotedItemRow item={item} index={index} />
          </GridCell>
        ))}
      </GridCells>
    </>
  );
}

function QuotedItemRow({
  item,
  index,
}: {
  item: MlrSummaryItem;
  index: number;
}) {
  const speaker = item.speaker === "doctor" ? "Doctor" : "Claimant";
  const ordinal = String(index + 1).padStart(2, "0");

  return (
    <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-start gap-x-2.5 px-4 py-3 transition-colors hover:bg-muted/40 sm:grid-cols-[5.5rem_minmax(0,1fr)] sm:gap-x-3 sm:px-5 sm:py-3.5">
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:text-[11px]">
          {speaker}
        </p>
        <p className="mt-0.5 text-[9px] font-medium uppercase tracking-wider text-muted-foreground sm:text-[10px]">
          {item.owner ? `for the ${item.owner}` : ordinal}
        </p>
      </div>
      <div className="min-w-0 pt-0.5">
        <p className="text-sm leading-snug text-foreground/90 sm:text-[15px] sm:leading-7">
          {item.text}
        </p>
        {/*
            The words the line rests on. Every quote here was checked
            against the transcript AND against the turns of the
            speaker it is credited to, so showing it is what makes
            the line checkable rather than merely asserted.
         */}
        <p className="mt-1.5 text-sm italic leading-relaxed text-muted-foreground">
          “{item.quote}”
        </p>
      </div>
    </div>
  );
}

export default SessionSummaryPanel;
